import { Component, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { ControlMenu } from '../control-menu';
import { useDropdown } from '@trimble/solidjs-utils';
import { usePortalMount } from '../portal/PortalMountContext';
import { useFloatingMenuPosition } from './useFloatingMenuPosition';
import type {
  ControlDropdownShellContext,
  ControlDropdownShellProps,
} from './control-dropdown.types';

/**
 * @intent Internal shell shared by ControlDropdown and SplitControlDropdown.
 * Owns the article wrapper, optional tooltip, floating-ui positioning, and the
 * portalled ControlMenu. Public components supply only the anchor markup and
 * click semantics via the render-prop context. Not exported from index.
 */
export const ControlDropdownShell: Component<ControlDropdownShellProps> = (
  props
) => {
  const { isOpen, toggle, ref, dropdownId, close } = useDropdown();
  const portalMount = usePortalMount();
  let animationRef: HTMLDivElement | undefined;
  let anchorRef: HTMLElement | undefined;

  const { menuStyle, opensUpward, setMenuSectionRef } = useFloatingMenuPosition(
    {
      getPosition: () => props.position,
      getAnchor: () => anchorRef,
    }
  );

  const anchorSectionClass = (): string => {
    const commonClasses = `flex rounded-sm ${props.customAnchorSectionClasses ?? ''}`;
    if (props.pressed) {
      return `${commonClasses} bg-[var(--modus-wc-color-primary)] text-white`;
    }
    if (props.isAnchorActive) {
      return `${commonClasses} bg-[var(--modus-wc-color-primary-pale)] hover:bg-[var(--modus-wc-color-primary-pale)]`;
    }
    return `${commonClasses} bg-base-page hover:bg-[var(--modus-wc-color-base-100)]`;
  };

  const anchorButtonHeightClass = (): string =>
    props.size === 'medium' ? 'min-h-10' : 'min-h-8';

  const ctx: ControlDropdownShellContext = {
    isOpen,
    toggle,
    close,
    anchorSectionClass,
    anchorButtonHeightClass,
  };

  const onMenuSectionRef = (el: HTMLElement | null): void => {
    setMenuSectionRef(el, () => {
      if (animationRef) {
        animationRef.classList.remove('opacity-0');
        animationRef.classList.add('opacity-100');
      }
    });
  };

  return (
    <article
      class="relative"
      ref={(el) => {
        ref(el as HTMLDivElement);
        anchorRef = el;
      }}
      data-testid="control-dropdown"
    >
      <Show when={props.tooltip} fallback={props.children(ctx)}>
        {(tooltip) => (
          <modus-wc-tooltip content={tooltip()} position="bottom">
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {props.children(ctx)}
          </modus-wc-tooltip>
        )}
      </Show>
      <Show when={isOpen()}>
        <Portal mount={portalMount()}>
          <section
            class="fixed pointer-events-auto z-[9999] overflow-visible"
            style={menuStyle()}
            data-testid="control-dropdown-content"
            data-dropdown-id={dropdownId}
            ref={(el) => onMenuSectionRef(el)}
          >
            {/* Fades only: a scale here would be applied while the menu is being
                measured for alignment, reporting mid-animation geometry. */}
            <div
              class="transition-opacity duration-150 ease-out opacity-0"
              ref={(el) => {
                animationRef = el;
              }}
            >
              <ControlMenu
                items={props.items}
                parentOpensUpward={opensUpward()}
                onClose={close}
              />
            </div>
          </section>
        </Portal>
      </Show>
    </article>
  );
};
